// hooks/six/useSixCatalog.ts
import { useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectCatalogSixes } from '@/store/selectors/sixCatalog';
import { fetchSixCatalog } from '@/store/thunks/sixCatalog/fetchSixCatalog';
import { showCreateSixModal } from '@/store/thunks/ui/uiThunk';
import type { Six } from '@/types/six/Six';
import { getSessionToken } from '@/utils/sessionStorage';

const NO_SIXES_LOADED = 0;

export interface SixWithConnection extends Six {
  connectionStatus: {
    isConnected: boolean;
    relayCount: number;
    peerCount: number;
  };
}

/**
 * Hook for Six Catalog page
 * Handles data fetching, navigation, and actions for the Six catalog
 *
 * @returns {Object} Hook state and handlers
 * @returns {Array} return.state.sixes - List of Six items with connection status
 * @returns {Object} return.handlers - Action handlers
 * @returns {Function} return.handlers.navigateToSix - Navigate to Six detail page
 * @returns {Function} return.handlers.createSix - Open create Six modal
 *
 * @example
 * const { state, handlers } = useSixCatalog();
 *
 * return (
 *   <Table
 *     data={state.sixes}
 *     onRowClick={handlers.navigateToSix}
 *   />
 * );
 */
export function useSixCatalog() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const sixes = useAppSelector(selectCatalogSixes);
  const connections = useAppSelector(state => state.p2pConnection.connections);

  const enrichedSixes: SixWithConnection[] = useMemo(
    () => sixes.map(six => {
      const conn = connections.find(c => c.sixId === six.id);
      if (conn === undefined) {
        return { ...six, connectionStatus: { isConnected: false, relayCount: 0, peerCount: 0 } };
      }
      return {
        ...six,
        connectionStatus: {
          isConnected: conn.status === 'connected',
          relayCount: conn.connectionDetails.filter(d => d.isRelay).length,
          peerCount: conn.connectionDetails.filter(d => !d.isRelay).length,
        },
      };
    }),
    [sixes, connections]
  );

  const sessionToken = getSessionToken();
  if(sessionToken === null) {
    throw new Error('No session token found');
  }

  useEffect(() => {
    if (sixes.length === NO_SIXES_LOADED) {
      void dispatch(fetchSixCatalog());
    }
  }, [dispatch, sixes.length]);

  const navigateToSix = useCallback(
    (sixId: string) => {
      void navigate({ to: `/app/sixes/${sixId}` });
    },
    [navigate]
  );

  const createSix = useCallback(() => {
    dispatch(showCreateSixModal());
  }, [dispatch]);

  return {
    state: { sixes: enrichedSixes },
    handlers: { navigateToSix, createSix },
  };
}
